/* eslint-disable @typescript-eslint/no-explicit-any */
import { Avatar, Button, Dropdown, Image, TableColumnsType, Tag } from "antd";
import TableUi from "../../atoms/StaticTable";
import { useRef, useState } from "react";
import { PopUp } from "../../atoms/PopUp";
import EmptyCard from "../../atoms/EmptyCard";
import { CrossIcon, DeleteIcon, NeedHelp } from "../../atoms/CustomIcons";
import ConfirmDelete from "../../atoms/ConfirmDelete";
import { MoreOutlined } from "@ant-design/icons";
import DashboardTemplate from "../../templates/Dashboard";
import Header from "../../atoms/Header";
import { getInitials } from "../../config/utils";
import TaskModal from "./Components/TaskModal";
import Filters from "./Components/Filters";
import taskTable from "./contractsData.json";
import { useNavigate } from "react-router-dom";

export default function Contracts() {
  const location = window.location.pathname;
  const navigate = useNavigate();
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableRecord] = useState<any | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [archivePopUp, setArchivePopUp] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<
    React.Key[] | string[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [archived, isArchived] = useState(false);

  const [filters, setFilters] = useState<{
    type: undefined | string;
    status: undefined | string;
    state: undefined;
    city: undefined;
    last_user_activity: string[] | undefined;
    onboarding_complete_date: string[] | undefined;
    search_text: undefined | string;
  }>({
    type: undefined,
    status: undefined,
    state: undefined,
    city: undefined,
    last_user_activity: undefined,
    onboarding_complete_date: undefined,
    search_text: undefined,
  });

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
          }}
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
        >
          Edit details
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="text"
          // onClick={() => {
          //   setIsDeleteModalOpen(true);
          // }}
          onClick={(e) => {
            e.stopPropagation();
          }}
          className="delete-hover"
          icon={<DeleteIcon />}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusClassMap: Record<string, string> = {
    Active: "accepted_tag",
    "Closure pending": "approval_pending_tag",
    "Termination pending": "sent_tag",
  };

  const serviceLineClassMap: Record<string, string> = {
    "landscape maintenance": "green_tag",
    "lawn care": "orange_tag",
    irrigation: "violet_tag",
    construction: "yellow_tag",
    "holiday lighting": "blue_tag",
    "snow services": "red_tag",
  };

  const columns: TableColumnsType = [
    {
      title: "Contract name",
      dataIndex: "contract_name",
      width: 320,
      render: (text) => text ?? "-",
      fixed: "left",
      sorter: (a, b) =>
        a.contract_name?.localeCompare(b.contract_name) ||
        a.contract_name?.length - b.contract_name?.length,
    },
    {
      title: "Property name",
      dataIndex: "property_name",
      width: 230,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.property_name?.localeCompare(b.property_name) ||
        a.property_name?.length - b.property_name?.length,
    },
    {
      title: "Service line",
      dataIndex: "service_line",
      width: 273,
      sorter: (a, b) =>
        a.service_line?.localeCompare(b.service_line) ||
        a.service_line?.length - b.service_line?.length,
      render: (text) => {
        if (!text) return "-";

        return text.split(",").map((item: string) => {
          const trimmed = item.trim();
          const cls =
            serviceLineClassMap[trimmed.toLowerCase()] || "default_tag";

          return (
            <Tag
              key={trimmed}
              className={cls}
              style={{
                textTransform: "capitalize",
                border: 0,
                marginRight: 4,
              }}
            >
              {trimmed}
            </Tag>
          );
        });
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 170,
      render: (text) => {
        const cls = statusClassMap[text] || "default_tag";
        return text ? <Tag className={cls}>{text}</Tag> : "-";
      },
      sorter: (a, b) =>
        a.status?.localeCompare(b.status) ||
        a.status?.length - b.status?.length,
    },
    {
      title: "Start date",
      dataIndex: "start_date",
      width: 130,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.start_date?.localeCompare(b.start_date) ||
        a.start_date?.length - b.start_date?.length,
    },
    {
      title: "End date",
      dataIndex: "end_date",
      width: 130,
      render: (text) => (text ? text : "-"),
      sorter: (a, b) =>
        a.end_date?.localeCompare(b.end_date) ||
        a.end_date?.length - b.end_date?.length,
    },
    {
      title: "Renewable",
      dataIndex: "renewable",
      width: 140,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {text ? text : "-"}
        </span>
      ),
      sorter: (a, b) =>
        a.renewable?.localeCompare(b.renewable) ||
        a.renewable?.length - b.renewable?.length,
    },
    {
      title: "Property category",
      dataIndex: "property_category",
      width: 163,
      render: (text) =>
        text ? <Tag className="default_tag">{text}</Tag> : "-",
      sorter: (a, b) =>
        a.property_category?.localeCompare(b.property_category) ||
        a.property_category?.length - b.property_category?.length,
    },
    {
      title: "Production manager",
      dataIndex: "production_manager",
      width: 270,
      sorter: (a, b) =>
        a.production_manager?.name?.localeCompare(b.production_manager?.name) ||
        a.production_manager?.name?.length - b.production_manager?.name?.length,
      render: (text) =>
        text?.name ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            {text?.image_url ? (
              <Avatar
                size={24}
                style={{ marginRight: 8 }}
                src={text?.image_url}
              />
            ) : (
              <Avatar size={24} style={{ marginRight: 8 }}>
                {getInitials(text?.name, "")}
              </Avatar>
            )}
            <span style={{ textTransform: "capitalize" }}>{text?.name}</span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Account manager",
      dataIndex: "account_manager",
      width: 180,
      sorter: (a, b) =>
        a.account_manager?.name?.localeCompare(b.account_manager?.name) ||
        a.account_manager?.name?.length - b.account_manager?.name?.length,
      render: (text) =>
        text?.name ? (
          <div style={{ display: "flex", alignItems: "center" }}>
            {text?.image_url ? (
              <Avatar
                size={24}
                style={{ marginRight: 8 }}
                src={text?.image_url}
              />
            ) : (
              <Avatar size={24} style={{ marginRight: 8 }}>
                {getInitials(text?.name, "")}
              </Avatar>
            )}
            <span style={{ textTransform: "capitalize" }}>{text?.name}</span>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Crew",
      dataIndex: "crew",
      width: 280,
      render: (text) => {
        if (!text) return "-";

        const parts = text
          .split(".")
          .map((item: string) => item.trim())
          .filter((item: string) => item);
        return (
          <>
            {parts.map((item: string) => {
              const trimmed = item.trim();
              return (
                <Tag
                  key={trimmed}
                  className="ant-tag default_tag"
                  style={{
                    marginRight: 4,
                    border: "1px solid #eff2f3 !important",
                  }}
                >
                  {trimmed}
                </Tag>
              );
            })}
          </>
        );
      },
      sorter: (a, b) =>
        a.crew?.localeCompare(b.crew) || a.crew?.length - b.crew?.length,
    },
    {
      title: "Pricing model",
      dataIndex: "pricing_model",
      width: 140,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {text ? text : "-"}
        </span>
      ),
      sorter: (a, b) =>
        a.pricing_model?.localeCompare(b.pricing_model) ||
        a.pricing_model?.length - b.pricing_model?.length,
    },
    {
      title: "Billing",
      dataIndex: "billing",
      width: 180,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {text ? text : "-"}
        </span>
      ),
      sorter: (a, b) =>
        a.billing?.localeCompare(b.billing) ||
        a.billing?.length - b.billing?.length,
    },
    {
      title: "Net terms",
      dataIndex: "net_terms",
      width: 180,
      render: (text) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {text ? text : "-"}
        </span>
      ),
      sorter: (a, b) =>
        a.net_terms?.localeCompare(b.net_terms) ||
        a.net_terms?.length - b.net_terms?.length,
    },
    {
      title: "Original contract revenue, $",
      dataIndex: "original_contract_revenue",
      width: 230,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.original_contract_revenue?.localeCompare(
          b.original_contract_revenue
        ) ||
        a.original_contract_revenue?.length -
          b.original_contract_revenue?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: "Projected revenue, $",
      dataIndex: "projected_revenue",
      width: 230,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.projected_revenue?.localeCompare(b.projected_revenue) ||
        a.projected_revenue?.length - b.projected_revenue?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: "Earned revenue to date, $",
      dataIndex: "earned_revenue_to_date",
      width: 230,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.earned_revenue_to_date?.localeCompare(b.earned_revenue_to_date) ||
        a.earned_revenue_to_date?.length - b.earned_revenue_to_date?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: "Original GM, %",
      dataIndex: "original_gm_percent",
      width: 180,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.original_gm_percent?.localeCompare(b.original_gm_percent) ||
        a.original_gm_percent?.length - b.original_gm_percent?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: "Original GM, $",
      dataIndex: "original_gm_dollar",
      width: 180,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.original_gm_dollar?.localeCompare(b.original_gm_dollar) ||
        a.original_gm_dollar?.length - b.original_gm_dollar?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: "Projected GM, %",
      dataIndex: "projected_gm_percent",
      width: 180,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.projected_gm_percent?.localeCompare(b.projected_gm_percent) ||
        a.projected_gm_percent?.length - b.projected_gm_percent?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: "Projected GM, $",
      dataIndex: "projected_gm_dollar",
      width: 180,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.projected_gm_dollar?.localeCompare(b.projected_gm_dollar) ||
        a.projected_gm_dollar?.length - b.projected_gm_dollar?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: "To date GM, %",
      dataIndex: "to_date_gm_percent",
      width: 180,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.to_date_gm_percent?.localeCompare(b.to_date_gm_percent) ||
        a.to_date_gm_percent?.length - b.to_date_gm_percent?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: "To date GM, $",
      dataIndex: "to_date_gm_dollar",
      width: 180,
      render: (text) => <span>{text ? text : "-"}</span>,
      sorter: (a, b) =>
        a.to_date_gm_dollar?.localeCompare(b.to_date_gm_dollar) ||
        a.to_date_gm_dollar?.length - b.to_date_gm_dollar?.length,
      align: "right",
      className: "right-aligned-sorter",
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{ items }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  const deleteFunction = () => {
    setLoading(true);
  };

  const deleteClientPopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          selectedRowKeys?.length > 0 ? (
            <>
              Are you sure you want to delete selected task?
              <br />
              This action cannot be undone.
            </>
          ) : (
            <>
              Are you sure you want to delete <b>tableRecord?.name</b>?<br />
              <br />
              This action cannot be undone.
            </>
          )
        }
        btnText={
          selectedRowKeys?.length > 0
            ? `Delete ${
                selectedRowKeys?.length === 1
                  ? selectedRowKeys?.length + " task"
                  : selectedRowKeys?.length + " tasks"
              }`
            : "Delete task"
        }
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const taskDetailModal = {
    visibility: isTaskModalOpen,
    class: "Task_Modal",
    content: (
      <TaskModal
        onClose={() => setIsTaskModalOpen(false)}
        tableRecord={tableRecord}
        loading={loading}
      />
    ),
    width: 1200,
    onCancel: () => setIsTaskModalOpen(false),
  };

  const archiveFunction = () => {
    setLoading(true);
  };

  const confirmArchivedPopUp = {
    visibility: archivePopUp,
    content: (
      <ConfirmDelete
        title=""
        isArchivePopup={true}
        onClose={() => setArchivePopUp(false)}
        heading='{tableRecord?.status === "active" ? "Archive" : "Activate"}'
        paragraph={
          <>
            Are you sure you want to{" "}
            {/* {tableRecord?.status === "active" ? "archive" : "activate"}{" "}
            <b>{tableRecord?.name}</b>? <br /> <br />
            {tableRecord?.status === "active" && (
              <>This task won’t be available for use.</>
            )} */}
          </>
        }
        btnText={`${
          "Activate"
          // tableRecord?.status === "active" ? "Archive" : "Activate"
        } task`}
        btnApi={archiveFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setArchivePopUp(false),
  };

  const onClickRow = () => {
    return {
      onClick: () => {
        navigate(`/contracts/profile/${companyId}`);
      },
    };
  };

  return (
    <DashboardTemplate loading={loading}>
      <Header
        title="Contracts list"
        description="View and manage company’s contracts."
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />
      <TableUi
        columns={columns}
        data={{
          count: taskTable?.length,
          data: taskTable || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            archived={archived}
            isArchived={isArchived}
            companyId={companyId}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        onClickRow={onClickRow}
      />
      {taskTable?.length === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              archived
                ? "There are no archived tasks"
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} tasks`
            }
            description={
              archived
                ? "We believe in our absolute success and hope to never have an archived tasks here."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : ""
            }
            image={
              archived
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar">
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "task" : "tasks"}`} selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...deleteClientPopUp} />
      <PopUp {...confirmArchivedPopUp} />
      <PopUp {...taskDetailModal} />
    </DashboardTemplate>
  );
}
